'use client';

import { Label } from '@repo/ui/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@repo/ui/components/ui/radio-group';

interface RisqueRuptureValue {
  [key: string]: string; // collet: 'faible', tronc: 'modere', etc.
}

interface RisqueRuptureTableProps {
  value: RisqueRuptureValue | undefined;
  onChange: (value: RisqueRuptureValue) => void;
  label: string;
  fieldId: string;
  error?: string;
}

const RISK_LEVELS = [
  { id: 'faible', label: 'Faible' },
  { id: 'modere', label: 'Modéré' },
  { id: 'eleve', label: 'Elevé' },
  { id: 'imminent', label: 'Imminent' }
];

const TREE_PARTS = [
  { id: 'collet', label: 'Collet' },
  { id: 'tronc', label: 'Tronc' },
  { id: 'houppier', label: 'Houppier' }
];

export function RisqueRuptureTable({
  value = {},
  onChange,
  label,
  fieldId,
  error
}: RisqueRuptureTableProps) {
  const handlePartValueChange = (partId: string, riskLevel: string) => {
    const newValue = {
      ...value,
      [partId]: riskLevel
    };
    onChange(newValue);
  };

  const getRiskLevelColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'faible':
        return 'text-green-600 bg-green-300 border-green-200';
      case 'modere':
        return 'text-black bg-yellow-200 border-yellow-200';
      case 'eleve':
        return 'text-white bg-red-500 border-orange-200';
      case 'imminent':
        return 'text-white bg-black border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="space-y-4">
      <Label className="text-sm font-medium">{label}</Label>

      <div className="border rounded-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 border-b">
          <div className="grid grid-cols-5 gap-0">
            <div className="p-3 font-medium text-sm border-r">Partie de l'arbre</div>
            {RISK_LEVELS.map((level) => (
              <div key={level.id} className="p-3 font-medium text-sm text-center border-r last:border-r-0">
                {level.label}
              </div>
            ))}
          </div>
        </div>

        {/* Rows */}
        {TREE_PARTS.map((part, partIndex) => (
          <div key={part.id} className={`${partIndex < TREE_PARTS.length - 1 ? 'border-b' : ''}`}>
            <div className="grid grid-cols-5 gap-0">
              {/* Part name */}
              <div className="p-3 font-medium text-sm border-r bg-gray-25">
                {part.label}
              </div>

              {/* Risk level radio buttons */}
              <RadioGroup
                value={value[part.id] || ''}
                onValueChange={(riskLevel) => handlePartValueChange(part.id, riskLevel)}
                className="contents"
              >
                {RISK_LEVELS.map((level, levelIndex) => (
                  <div key={level.id} className={`p-3 flex justify-center items-center border-r last:border-r-0 ${value[part.id] === level.id ? getRiskLevelColor(level.id) : 'hover:bg-gray-50'
                    }`}>
                    <RadioGroupItem
                      value={level.id}
                      id={`${fieldId}-${part.id}-${level.id}`}
                      className="h-4 w-4"
                    />
                  </div>
                ))}
              </RadioGroup>
            </div>
          </div>
        ))}
      </div>

      {/* Summary */}
      {Object.keys(value).length > 0 && (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium mb-2">Évaluation actuelle :</h4>
          <div className="space-y-1">
            {TREE_PARTS.map((part) => {
              const riskLevel = value[part.id];
              if (!riskLevel) return null;

              const riskLabel = RISK_LEVELS.find(r => r.id === riskLevel)?.label;

              return (
                <div key={part.id} className="flex justify-between text-sm">
                  <span>{part.label}:</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getRiskLevelColor(riskLevel)}`}>
                    {riskLabel}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <p className="text-sm text-red-600 mt-1">{error}</p>
      )}
    </div>
  );
}
